<template>
  <div>
    <v-row dense align-content="center" class="mb-2">
      <v-col cols="auto">
        <v-switch
          v-model="showPath"
          class="mt-0 px-2 v-input--reverse"
          dense
          hide-details
          @change="onShowPathInput"
        >
          <template #label>
            <span style="color: black">{{ $t("global.run.show-path") }}</span>
          </template>
        </v-switch>
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-btn
          color="primary"
          :disabled="downloadingZip"
          :loading="downloadingZip"
          @click="onDownloadZip(downloadZipLink)"
        >
          {{ $t("global.run.download-all") }}
        </v-btn>
      </v-col>
    </v-row>

    <ooliba-basic-table
      :headers="headers"
      :items="itemsFiltered"
      :loading="busy"
      sort-by="name"
      no-pagination
    >
      <template #[`item.env`]="{ item }">
        <v-chip small outlined v-if="item.envIsLocal">LOCAL</v-chip>
        <file-env-chip
          v-if="!item.envIsLocal && item.env"
          :color="item.envColor"
          :label="item.env"
        >
        </file-env-chip>
      </template>

      <template #[`item.name`]="{ item }">
        <router-link
          v-if="item.publishedFileId"
          class="no-underline"
          :to="`/file/view/${item.publishedFileId}/${item.version}`"
        >
          {{ item.name }}
        </router-link>
      </template>

      <template #[`item.publish`]="{ item }">
        <v-tooltip top v-if="item.showPublish">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" v-if="item.showPublish" @click="onPublish(item)">
              pic-save
            </v-icon>
          </template>
          <span>{{ $t("global.msg.save-details") }}</span>
        </v-tooltip>
      </template>

      <template #[`item.download`]="{ item }">
        <v-icon v-if="item.href && !item.downloading" @click="onDownload(item)">
          pic-import
        </v-icon>

        <v-progress-circular :size="23" v-if="item.downloading" indeterminate />
      </template>

      <template #[`item.open`]="{ item }">
        <v-icon v-if="isExcelFile(item.name)" @click="onOpen(item)">
          mdi-eye-outline
        </v-icon>
      </template>

      <template #[`item.size`]="{ item }">
        <span v-if="item.publishedFileId">
          {{ `${Math.round(item.size / 1000)} KB` }}
        </span>
      </template>
    </ooliba-basic-table>

    <v-dialog v-model="showSpreadSheet" persistent>
      <v-overlay absolute :value="isLoadingSpreadsheet">
        <v-chip color="blue" dark>{{ $t("global.msg.loading") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-icon
          large
          @click="onCloseSpreadsheet"
          color="error"
          style="float: right"
        >
          mdi-alpha-x-box-outline
        </v-icon>

        <v-card-text>
          <iframe
            :src="excelUrl"
            width="100%"
            title="Excel preview"
            class="mt-1"
            ref="excelHtml"
            style="height: 74vh"
            v-if="showSpreadSheet"
            @load="handleExcelLoaded"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { get, downloadFile, put, getIframeToken } from "@/model/api";

import OolibaBasicTable from "@/components/OolibaBasicTable";
import FileEnvChip from "@/components/FileEnvChip";

import { isExcelFile } from "@/model/util";

export default {
  name: "RunOutputFiles",
  components: { OolibaBasicTable, FileEnvChip },
  props: {
    run: {},
  },

  data() {
    return {
      busy: true,
      headers: [
        {
          text: this.$t("global.environment.env"),
          value: "env",
          align: "center",
          width: 108,
        },
        { text: this.$t("global.concepts.name"), value: "name" },
        { text: this.$t("global.concepts.size"), value: "size", width: 100 },
        {
          text: this.$t("global.action.save"),
          value: "publish",
          align: "center",
          width: 50,
          sortable: false,
        },

        {
          text: this.$t("global.action.view"),
          value: "open",
          align: "center",
          width: 50,
          sortable: false,
        },

        {
          text: this.$t("global.action.download"),
          value: "download",
          align: "center",
          width: 50,
          sortable: false,
        },
      ],
      items: [],
      outputFileInfoes: [],

      showSpreadSheet: false,
      isLoadingSpreadsheet: false,
      excelUrl: "",
      excelItemId: undefined,

      showPath: false,

      downloadingZip: false,
    };
  },

  computed: {
    itemsFiltered() {
      let items = [...this.items];
      if (!this.showPath && items && items.length) {
        items = items.map((item) => {
          let itemWithPathRemove = { ...item };
          let filename = itemWithPathRemove.name;

          itemWithPathRemove.name =
            filename && filename.length
              ? `${filename}`.substring(filename.lastIndexOf("/") + 1)
              : filename;
          return itemWithPathRemove;
        });
      }
      return items;
    },

    downloadZipLink() {
      return "simulationRun/" + this.run.infoId + "/download-output-files";
    },
  },

  watch: {
    run: {
      immediate: true,

      async handler(newRun, oldRun) {
        if (newRun && newRun.infoId && newRun.status !== oldRun?.status) {
          this.busy = true;
          this.items = [];
          this.outputFileInfoes = await this.getOutputFileInfoes();
          this.items = this.getItems();
          this.busy = false;
        }
      },
    },
  },

  methods: {
    isExcelFile,

    handleExcelLoaded() {
      const iframe = this.$refs.excelHtml;
      const pre = iframe.contentDocument.querySelector("body > pre");
      if (pre) {
        const serverError = pre.textContent;
        if (serverError.includes("Max size exceeded")) {
          this.excelUrl = "";
          this.$store.commit("showWarning", this.$t("global.msg.fichierGrand"));
        } else if (serverError.includes("File not found or empty")) {
          this.excelUrl = "";
          this.$store.commit(
            "showWarning",
            this.$t("global.msg.missingEmptyTemplateFile")
          );
        }
      }
      this.isLoadingSpreadsheet = false;
    },

    onCloseSpreadsheet() {
      this.showSpreadSheet = false;

      // Switching tabs in the Excel preview inserts elements into the history stack, so we need to remove these when the preview is closed
      // OOL-2629
      get(`/simulationRun/workbook-html-sheet-requests/${this.excelItemId}`)
        .then((r) => {
          if (r > 1) {
            window.history.go(-r + 1);
          }
        })
        .catch((err) => this.$store.commit("showError", err));
    },

    onShowPathInput(value) {
      this.$store.commit("setShowPath", !!value);
    },

    getItems() {
      if (!this.outputFileInfoes) return [];
      let items = [];
      this.outputFileInfoes.forEach((value) => {
        const item = {};

        item.id = value.id;
        item.name = value.name;
        item.published = value.published;
        item.publishable = value.publishable;
        item.showPublish =
          !value.published &&
          value.publishable &&
          this.$store.state.runCreatePermission;
        item.publishedFileId = value.publishedFileId;
        item.env = value.fileEnvLabel;
        item.envColor = value.fileEnvColor;
        item.envIsLocal = value.fileEnvIsLocal;
        if (value.fileVersionSize) {
          item.size = value.fileVersionSize;
        }
        item.version = value.fileVersionNumber + 1;
        item.href = `simulationRun/output-file-info/${value.id}/download`;
        item.downloading = false;

        items.push(item);
      });
      return items;
    },

    async getOutputFileInfoes() {
      const path =
        "/simulationRun/run-info/" + this.run.infoId + "/output-file-info";

      return await get(path).catch((err) =>
        this.$store.commit("showError", err)
      );
    },

    async onDownload(item) {
      item.downloading = true;
      const href = item.href;
      await downloadFile(href).catch((err) => {
        this.$store.commit("showError", err);
      });
      item.downloading = false;
    },

    async onDownloadZip(href) {
      this.downloadingZip = true;
      await downloadFile(href).catch((err) => {
        this.$store.commit("showError", err);
      });
      this.downloadingZip = false;
    },

    async onOpen(item) {
      this.isLoadingSpreadsheet = true;
      this.excelItemId = item.id;
      this.showSpreadSheet = true;
      try {
        const { token } = await getIframeToken(
          "simulationRun/output-file-info",
          item.id
        );
        this.excelUrl = `/compliance-server/api/simulationRun/output-file-info/workbook-html/${item.id}/index.html?iframeToken=${token}`;
      } catch (err) {
        this.$store.commit("showError", err);
        this.isLoadingSpreadsheet = false;
      }
    },

    async onPublish(item) {
      const row = this.items.findIndex((i) => i.id === item.id);
      return await put(
        "/simulationRun/import-output?runInfoId=" +
          this.run.infoId +
          "&runOutputId=" +
          item.id
      )
        .then((r) => {
          item = this.items[row];
          item.env = r.fileEnvLabel;
          item.envColor = r.fileEnvColor;
          item.envIsLocal = r.fileEnvIsLocal;
          item.showPublish = false;
          item.publishedFileId = r.publishedFileId;
          item.size = r.size;
          this.items = [...this.items]; // this is necessary to trigger an update of itemsFiltered
        })
        .catch((err) => this.$store.commit("showError", err));
    },
  },

  created() {
    this.showPath = this.$store.getters.isShowPath;
  },
};
</script>
